// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import api from '../api/client'
import toast from 'react-hot-toast'
import { Download, FileText, Package, AlertCircle } from 'lucide-react'

interface Release {
  id: string
  version: string
  release_date: string
  release_notes: string | null
  changelog: string | null
  file_size: number | null
  checksum_sha256: string | null
  is_latest: boolean
  is_mandatory: boolean
  is_beta: boolean
}

interface Document {
  id: string
  title: string
  description: string
  filename: string
  file_size: number
  download_url: string
}

export default function Downloads() {
  const [releases, setReleases] = useState<Release[]>([])
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [downloading, setDownloading] = useState<string | null>(null)

  useEffect(() => {
    loadDownloads()
  }, [])

  const loadDownloads = async () => {
    try {
      const [relRes, docRes] = await Promise.all([
        api.get('/api/portal/downloads'),
        api.get('/api/portal/downloads/docs')
      ])
      setReleases(relRes.data)
      setDocuments(docRes.data)
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error('Active subscription required to access downloads')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = async (version: string) => {
    setDownloading(version)
    try {
      const res = await api.get(`/api/portal/downloads/${version}/package`)
      window.open(res.data.download_url, '_blank')
      toast.success('Download started')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to get download URL')
    } finally {
      setDownloading(null)
    }
  }

  const handleDocDownload = async (docId: string) => {
    try {
      const res = await api.get(`/api/portal/downloads/docs/${docId}/download`)
      window.open(res.data.download_url, '_blank')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to download document')
    }
  }

  const formatSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown size'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  const latestRelease = releases.find(r => r.is_latest)
  const otherReleases = releases.filter(r => !r.is_latest)

  return (
    <div className="space-y-6 fade-in">
      <h1 className="text-2xl font-bold">Downloads</h1>

      {/* Latest Release */}
      {latestRelease && (
        <div className="card border-primary-500">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-6 h-6 text-primary-500" />
                <h2 className="text-xl font-semibold">Exploit Score v{latestRelease.version}</h2>
                <span className="badge badge-success">Latest</span>
                {latestRelease.is_beta && <span className="badge badge-warning">Beta</span>}
              </div>
              <p className="text-gray-400">
                Released {new Date(latestRelease.release_date).toLocaleDateString()}
                {latestRelease.file_size && ` • ${formatSize(latestRelease.file_size)}`}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Full installation package — for new deployments. Existing
                installs update in-app from Admin → System Updates.
              </p>
              {latestRelease.release_notes && (
                <p className="text-gray-300 mt-3">{latestRelease.release_notes}</p>
              )}
            </div>
            <button
              onClick={() => handleDownload(latestRelease.version)}
              disabled={downloading === latestRelease.version}
              className="btn btn-primary"
            >
              {downloading === latestRelease.version ? (
                <div className="spinner w-5 h-5"></div>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download Installer
                </>
              )}
            </button>
          </div>
          {latestRelease.checksum_sha256 && (
            <div className="mt-4 p-3 bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">SHA-256 Checksum</p>
              <code className="text-xs text-gray-300 break-all">{latestRelease.checksum_sha256}</code>
            </div>
          )}
        </div>
      )}

      {/* Previous Releases */}
      {otherReleases.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Previous Versions</h2>
          <div className="space-y-4">
            {otherReleases.map((release) => (
              <div key={release.id} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">v{release.version}</span>
                    {release.is_beta && <span className="badge badge-warning text-xs">Beta</span>}
                    {release.is_mandatory && <span className="badge badge-danger text-xs">Required Update</span>}
                  </div>
                  <p className="text-sm text-gray-400">
                    {new Date(release.release_date).toLocaleDateString()}
                    {release.file_size && ` • ${formatSize(release.file_size)}`}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(release.version)}
                  disabled={downloading === release.version}
                  className="btn btn-secondary"
                >
                  {downloading === release.version ? (
                    <div className="spinner w-4 h-4"></div>
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      {documents.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-1">Installation Guides</h2>
          <p className="text-sm text-gray-400 mb-4">
            Step-by-step guides to get your deployment running. Full product
            documentation is available inside the app.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg">
                <FileText className="w-10 h-10 text-blue-500 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium">{doc.title}</h3>
                  <p className="text-sm text-gray-400">{doc.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatSize(doc.file_size)}</p>
                </div>
                <button
                  onClick={() => handleDocDownload(doc.id)}
                  className="btn btn-secondary"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Downloads Available */}
      {releases.length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Downloads Available</h2>
          <p className="text-gray-400">
            Downloads are available for customers with an active subscription.
          </p>
        </div>
      )}
    </div>
  )
}
